import React, { useCallback, useEffect, useState } from "react";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import FolderIcon from "@mui/icons-material/Folder";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PostAddIcon from "@mui/icons-material/PostAdd";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Card,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { orgSessionManager } from "src/domains/auth/services/orgSessionManager";
import { ORGANIZATIONS, PROJECTS } from "src/platform/constants/routes";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { getCardStyles, sectionLabelSx } from "src/shell/styles/themeUtils";

import { useOrgRealtime } from "../../hooks/useOrgRealtime";

interface OrgMetricsPanelProps {
  orgAlias: string;
  onNavigateBilling?: () => void;
  onInviteMember?: () => void;
}

interface ProjectRow {
  alias: string;
  name: string;
  stackCount: number;
}

function stackSpend(
  billing: ReturnType<typeof useOrgRealtime>["billing"] | null | undefined,
  alias: string,
): string | null {
  if (!billing?.systems) return null;
  const breakdown = billing.systems.find((s) => s.systemAlias === alias);
  if (!breakdown) return null;
  if (breakdown.dataQuality === "cold" || breakdown.dataQuality === "empty")
    return null;
  return breakdown.totalUsd;
}

export function OrgMetricsPanel({
  orgAlias,
  onNavigateBilling,
  onInviteMember,
}: OrgMetricsPanelProps) {
  const theme = useTheme();
  const { stacks, billing, billingLoading } = useOrgRealtime(orgAlias);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const token = orgSessionManager.getToken() ?? "";
      const [projectsRes, appsRes] = await Promise.all([
        fetch(
          `${apiBaseUrls.BACKEND}/api/v1/projects?organizationAlias=${encodeURIComponent(orgAlias)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        fetch(
          `${apiBaseUrls.BACKEND}/api/v1/applications?organizationAlias=${encodeURIComponent(orgAlias)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        ),
      ]);
      const projectList = projectsRes.ok
        ? ((await projectsRes.json()) as { alias: string; name: string }[])
        : [];
      const appList = appsRes.ok
        ? ((await appsRes.json()) as {
            alias: string;
            project?: { alias: string };
            projectAlias?: string;
          }[])
        : [];
      const counts = new Map<string, number>();
      for (const app of appList) {
        const key = app.project?.alias ?? app.projectAlias ?? "unassigned";
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
      setProjects(
        projectList.map((p) => ({
          alias: p.alias,
          name: p.name,
          stackCount: counts.get(p.alias) ?? 0,
        })),
      );
    } catch {
      setProjects([]);
    } finally {
      setProjectsLoading(false);
    }
  }, [orgAlias]);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  const orgQuality = billing?.dataQuality;
  const isFree = billing?.plan === "free";
  const totalLabel = !billing
    ? "—"
    : orgQuality === "cold"
      ? "Warming up…"
      : orgQuality === "empty"
        ? "No data yet"
        : isFree
          ? `$${billing.totalUsd} (free)`
          : `$${billing.totalUsd}`;

  const spendRows = stacks
    .map((s) => ({
      alias: s.alias,
      spend: stackSpend(billing, s.alias),
    }))
    .filter((r): r is { alias: string; spend: string } => r.spend !== null)
    .slice(0, 5);

  const cardSx = getCardStyles(theme);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Card sx={cardSx}>
          <Typography sx={{ ...sectionLabelSx, mb: 1.5 }}>
            Cloud Spend
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.success.main, 0.08),
                color: theme.palette.success.main,
                flexShrink: 0,
              }}
            >
              <AttachMoneyIcon fontSize="small" />
            </Box>
            <Box>
              {billingLoading ? (
                <CircularProgress size={16} />
              ) : (
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color={
                    orgQuality === "cold" ? "text.secondary" : "text.primary"
                  }
                >
                  {totalLabel}
                </Typography>
              )}
              {orgQuality === "cold" && (
                <WarningAmberIcon fontSize="small" color="warning" />
              )}
            </Box>
          </Stack>
          {billing && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, mb: 1.5 }}
            >
              {new Date(billing.periodStart).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
              {" – "}
              {new Date(billing.periodEnd).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </Typography>
          )}
          {spendRows.length > 0 && (
            <Stack spacing={0}>
              {spendRows.map((row, i) => (
                <Stack
                  key={row.alias}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    py: 1,
                    px: 1.5,
                    borderBottom:
                      i < spendRows.length - 1
                        ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                        : "none",
                  }}
                >
                  <Typography
                    variant="body2"
                    fontFamily="monospace"
                    fontWeight={500}
                  >
                    {row.alias}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ${row.spend}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Stack spacing={2}>
          <Card sx={cardSx}>
            <Typography sx={{ ...sectionLabelSx, mb: 1.5 }}>
              Top Projects
            </Typography>
            {projectsLoading ? (
              <CircularProgress size={16} />
            ) : projects.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No projects yet.
              </Typography>
            ) : (
              <Stack spacing={0}>
                {projects.slice(0, 2).map((p, i, arr) => (
                  <Stack
                    key={p.alias}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      py: 1,
                      px: 1.5,
                      borderBottom:
                        i < arr.length - 1
                          ? `1px solid ${alpha(theme.palette.divider, 0.06)}`
                          : "none",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FolderIcon
                        fontSize="small"
                        sx={{ color: "text.disabled" }}
                      />
                      <Typography variant="body2" fontWeight={600}>
                        {p.name}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {p.stackCount} stack{p.stackCount !== 1 ? "s" : ""}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}
          </Card>

          <Card sx={cardSx}>
            <Typography sx={{ ...sectionLabelSx, mb: 1.5 }}>
              Quick Actions
            </Typography>
            <Stack spacing={1}>
              <QuickActionRow
                icon={<GroupAddIcon fontSize="small" />}
                label="Invite member"
                href={ORGANIZATIONS.MEMBERS.getPath(orgAlias)}
                onClick={onInviteMember}
              />
              <QuickActionRow
                icon={<PostAddIcon fontSize="small" />}
                label="Create project"
                href={PROJECTS.CREATE.getPath(orgAlias)}
              />
              <QuickActionRow
                icon={<ReceiptLongIcon fontSize="small" />}
                label="View billing"
                onClick={onNavigateBilling}
              />
            </Stack>
          </Card>
        </Stack>
      </Grid>
    </Grid>
  );
}

interface QuickActionRowProps {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
}

function QuickActionRow({ icon, label, href, onClick }: QuickActionRowProps) {
  return (
    <Box
      component={href ? "a" : "button"}
      href={href}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        width: "100%",
        p: 1.25,
        borderRadius: 1,
        bgcolor: "transparent",
        border: "none",
        cursor: "pointer",
        textDecoration: "none",
        color: "text.primary",
        transition: "background 150ms ease",
        "&:hover": {
          bgcolor: alpha("#6366f1", 0.04),
        },
      }}
    >
      <Box sx={{ color: "primary.main", display: "flex" }}>{icon}</Box>
      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.disabled">
        →
      </Typography>
    </Box>
  );
}

export default OrgMetricsPanel;
